import React, { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { SplashScreen } from '@/components/SplashScreen';
import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { syncPendingRides } from '@/lib/offline';

import Landing from '@/pages/Landing';
import Onboarding from '@/pages/Onboarding';
import Home from '@/pages/Home';
import StartRide from '@/pages/StartRide';
import VehiclePhoto from '@/pages/VehiclePhoto';
import VehicleCheck from '@/pages/VehicleCheck';
import FamilyDashboard from '@/pages/FamilyDashboard';
import Install from '@/pages/Install';
import FamilyJoin from '@/pages/FamilyJoin';
import About from '@/pages/About';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import ActiveRide from '@/pages/ActiveRide';
import PublicShare from '@/pages/PublicShare';
import SafetyMode from '@/pages/SafetyMode';
import Rides from '@/pages/Rides';
import Contacts from '@/pages/Contacts';
import Account from '@/pages/Account';
import Admin from '@/pages/Admin';
import AuthCallback from '@/pages/AuthCallback';

// Gate family-only routes behind the runtime feature flag. While config is still
// loading we render nothing (avoids a flash); once loaded, redirect if OFF.
function FamilyGate({ children }) {
  const familyMode = useApp((s) => s.familyMode);
  const configLoaded = useApp((s) => s.configLoaded);
  if (!configLoaded) return null;
  if (!familyMode) return <Navigate to="/app" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const setUser = useApp((s) => s.setUser);
  const setAuthChecked = useApp((s) => s.setAuthChecked);
  const setConfig = useApp((s) => s.setConfig);
  const sosRideId = useApp((s) => s.sosRideId);
  const resumedRef = React.useRef(false);

  useEffect(() => {
    // Load runtime feature flags (family mode on/off) as early as possible.
    api.config().then(setConfig).catch(() => setConfig({ family_mode_enabled: false }));
    // If returning from OAuth, let AuthCallback handle it (skip /me check).
    if (window.location.hash?.includes('session_id=')) { setAuthChecked(true); return; }
    api.me().then((u) => setUser(u)).catch(() => setUser(null)).finally(() => setAuthChecked(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync any rides that were created offline (idempotent) whenever we're online.
  useEffect(() => {
    const doSync = () => syncPendingRides(
      (payload) => api.createRide(payload),
      (rideId, pt) => api.postLocation(rideId, pt),
    ).catch((e) => console.debug('global ride sync failed:', e));
    window.addEventListener('online', doSync);
    if (navigator.onLine) doSync();
    return () => window.removeEventListener('online', doSync);
  }, []);

  // If the app was reopened while an SOS is active, resume the safety screen.
  useEffect(() => {
    if (resumedRef.current) return;
    resumedRef.current = true;
    const target = `/app/safety/${sosRideId}`;
    if (sosRideId && location.pathname !== target && !window.location.hash?.includes('session_id=')) {
      navigate(target, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Process OAuth callback synchronously during render (avoids race conditions).
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/install" element={<Install />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/app" element={<Home />} />
      <Route path="/app/start" element={<StartRide />} />
      <Route path="/app/vehicle" element={<VehicleCheck />} />
      <Route path="/app/family" element={<FamilyGate><FamilyDashboard /></FamilyGate>} />
      <Route path="/app/ride/:id/photo" element={<VehiclePhoto />} />
      <Route path="/app/ride/:id" element={<ActiveRide />} />
      <Route path="/app/safety/:id" element={<SafetyMode />} />
      <Route path="/app/rides" element={<Rides />} />
      <Route path="/app/contacts" element={<Contacts />} />
      <Route path="/app/account" element={<Account />} />
      <Route path="/app/settings" element={<Account />} />
      <Route path="/r/:token" element={<PublicShare />} />
      <Route path="/family/join/:token" element={<FamilyGate><FamilyJoin /></FamilyGate>} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />

      {/* Legacy redirects to keep old links / bookmarks working */}
      <Route path="/home" element={<Navigate to="/app" replace />} />
      <Route path="/start" element={<Navigate to="/app/start" replace />} />
      <Route path="/rides" element={<Navigate to="/app/rides" replace />} />
      <Route path="/contacts" element={<Navigate to="/app/contacts" replace />} />
      <Route path="/account" element={<Navigate to="/app/account" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <SplashScreen />
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
